import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Divider, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";

import { store, useAppSelector } from '@/app/store';
import { ChannelInfo } from '@/features/channels/channelsSlice';
import { TrackFormatInfo, copyTrackFormatInfo, copyTrackFormatsHolder, getNewTrackFormatInfo } from '@/features/trackFormats/trackFormatsSlice';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import { NumberText } from '@/components/Common/Field';
import MenuItem from '@/components/Common/MenuItem';
import { DeleteModal, RightBar } from '@/components/Common/Modals';

import Channels from '@/pages/Channels/Channels';
import Projects from '@/pages/Projects/Projects';
import TrackFormat from './TrackFormat';
import { RoleContext } from '@/app/api/common';
import { usePageSubscription } from '@/app/websocket/hooks';

const TrackFormats = () => {
  const { t } = useTranslation();

  usePageSubscription([{ type: 'track_format' }]);

  const trackFormats = useAppSelector((state) => state.trackFormats);
  const navigate = useNavigate();
  const location = useLocation();
  const { tf_key } = useParams();
  const [cols, setCols] = React.useState([] as any[]);
  const [rows, setRows] = React.useState([] as TrackFormatInfo[]);
  const [deleteNum, setDeleteNum] = React.useState(-1);
  const role = React.useContext(RoleContext);

  const isSearchChannel = location.pathname.endsWith('/searchchannel');
  const isSearchProject = location.pathname.endsWith('/searchproject');

  const [channelRows, setChannelRows] = React.useState([] as ChannelInfo[]);

  const tfNum = React.useMemo(() => {
    if (!tf_key) return -1;
    if (tf_key === '__new__') return -2;
    if (isSearchChannel || isSearchProject) return -1;
    const idx = trackFormats?.track_formats?.findIndex((tf: any) => tf.key === tf_key);
    return idx !== undefined && idx >= 0 ? idx : -1;
  }, [tf_key, trackFormats.track_formats, isSearchChannel, isSearchProject]);

  React.useEffect(() => {
    store.getTrackFormats();
  }, []);

  React.useEffect(() => {
    if (isSearchChannel && tf_key) {
      store.getChannelsByTrackFormat(tf_key).then((channels) => {
        setChannelRows(channels || []);
      });
    }
  }, [isSearchChannel, tf_key]);

  React.useEffect(() => {
    if (isSearchProject && tf_key) {
      store.clearChannelListProjects();
      store.getProjectsByTrackFormat(tf_key);
    }
  }, [isSearchProject, tf_key]);


  React.useEffect(() => {
    setCols([
    {
        field:			"key",
        headerName:		t("track_formats:key"),
        flex:			1,
        minWidth:		200,
    },
    {
        field:			"description",
        headerName:		t("track_formats:description"),
        flex:			1,
        minWidth:		200,
    },
    {
        field:			"options",
        headerName:		t("track_formats:options"),
        flex:			0,
        filter:			false,
        minWidth:		90,
        maxWidth:		90,
        cellRenderer:	OptionsCell,
    },
    ]);

    const rws: TrackFormatInfo[] = [];
    for (let i = 0; i < trackFormats?.track_formats?.length; i++) {
      let tf = copyTrackFormatInfo(trackFormats?.track_formats[i]);
      let tfAny = tf as any;

      tfAny.requestDelete = () => setDeleteNum(i);
      tfAny.requestEdit = () => navigate('/settings/trackformats/' + encodeURIComponent(tf.key));
      tfAny.__index__ = i;

      rws.push(tf);
    }

    setRows(rws);
  }, [trackFormats.track_formats, t]);

  const updateIsOpen = (val: boolean) => {
    if (!val)
      navigate('/settings/trackformats');
  };

  const processDelete = React.useCallback(async (_obj: Object, answer: string) => {
    setDeleteNum(-1);

    if (answer !== "yes") {
      return;
    }

    if (deleteNum < -1)
      return;

    const ntfs = copyTrackFormatsHolder(trackFormats);
    if (	!ntfs ||
        	ntfs?.track_formats?.length <= deleteNum
       )
      return;

    ntfs.track_formats.splice(deleteNum, 1);

    const mess = await store.setTrackFormats(ntfs);
    console.log("TrackFormats/processDelete/deleteNum=" + deleteNum + "/mess=", mess);
  }, [trackFormats.track_formats, deleteNum]);

  const updateTrackFormat = React.useCallback(async (n: number, tf: TrackFormatInfo) => {
    const ntfs = copyTrackFormatsHolder(trackFormats);
    if (	!tf ||
    		n == -1 ||
    		!ntfs
       )
      return;

    const ntf = copyTrackFormatInfo(tf);
    if (n == -2) {
      ntfs.track_formats.push(ntf);
    } else {
      ntfs.track_formats[n] = ntf;
    }

    const mess = await store.setTrackFormats(ntfs);
    console.log("TrackFormats/updateTrackFormat/n=" + n + "/mess=", mess);
  }, [trackFormats.track_formats]);

  const handleCellClick = React.useCallback((evt: any) => {
    if (evt.colDef.field == "options")
      return;
    if (!role?.admin && !role?.privilages?.includes("ChangeTrackFormats"))
      return;
    navigate('/settings/trackformats/' + encodeURIComponent(evt.data.key));
  }, [navigate]);

  const getTfDesc = React.useCallback((n: number): TrackFormatInfo | null => {
    if (n == -2)
      return getNewTrackFormatInfo();

    if (	n < 0 ||
    		n >= rows?.length
       )
      return null;

    return rows[n];
  }, [rows]);

  if (isSearchChannel && tf_key) {
    return (
    <React.Fragment>
      <FlexRow direction="column"
               align="start"
               className={[
                 'bg-white',
                 'px-[32px] pt-[32px] sticky top-[64px] z-10',
               ]}>
      </FlexRow>
      <FlexRow direction="column"
               align="start"
               className={["py-[16px]", "px-[32px]"]}>
        <Channels hideContainer={true}
                  externalChannels={channelRows} />
      </FlexRow>
    </React.Fragment>
    );
  }

  if (isSearchProject && tf_key) {
    return (
    <React.Fragment>
      <FlexRow direction="column"
               align="start"
               className={[
                 'bg-white',
                 'px-[32px] pt-[32px] sticky top-[64px] z-10',
               ]}>
      </FlexRow>
      <FlexRow direction="column"
               align="start"
               className={["py-[16px]", "px-[32px]"]}>
        <Projects hideContainer={true} />
      </FlexRow>
    </React.Fragment>
    );
  }

  return (
  <React.Fragment>
    <FlexRow direction="column"
	         align="start"
	         className={[
               'bg-white',
               'px-[32px] pt-[32px] sticky top-[64px] z-10',
             ]}>
      <div className="text-[32px]/[48px]">
        { t('track_formats:track_formats') }
      </div>
      <div>
        <NumberText>
          { rows?.length }
        </NumberText>
        { ' ' +
          t('track_formats:total_track_formats')
        }
      </div>
      <Divider className="my-[8px]" />
      { (role?.admin || role?.privilages?.includes("ChangeTrackFormats")) &&
        <Button caption={t('track_formats:new_track_format')}
                icon="add"
                className="uppercase mt-[20px]"
                onClick={() => navigate('/settings/trackformats/__new__')} />
      }
    </FlexRow>
    <FlexRow direction="column"
             align="start"
             className={[
               "py-[16px]",
               "px-[32px]"
             ]}>

      <Grid rowData={rows}
            columnDefs={cols}
            filter={false}
            globalFilter={true}
            showPages={true}
            maxHeight="calc(100vh - 380px)"
            onCellClicked={handleCellClick}
            pageSize={10} />
    </FlexRow>
          {tfNum != -1 &&
              <RightBar onOpenChange={updateIsOpen}>
                  <TrackFormat trackFormat={getTfDesc(tfNum)}
                      index={tfNum}
                      setTrackFormat={(tf: TrackFormatInfo) => updateTrackFormat(tfNum, tf)}
                      onClose={() => navigate('/settings/trackformats')} />
              </RightBar>
          }
  {deleteNum > -1 && deleteNum < rows.length &&
	<DeleteModal title={t('track_formats:delete_track_format_:caption')}
	             message={t('track_formats:delete_track_format_:message_n', {n: rows[deleteNum]?.key})}
	             onResult={processDelete}>
      <>
      </>
    </DeleteModal>
  }
  </React.Fragment>
  );
};

const OptionsCell = (props: any) => {
  const { t } = useTranslation();

  const [open, setOpen] = React.useState(false);
  const role = React.useContext(RoleContext);
  const handleEdit = React.useCallback(() => {
    setOpen(false);
    props.data.requestEdit();
  }, [setOpen, props.data]);

  const handleDelete = React.useCallback(async () => {
    setOpen(false);
    props.data.requestDelete();
  }, [setOpen, props.data]);
  return (
  <Popover key={1}
           isOpen={open}
           placement="bottom-end"
           classNames={{
             content: "rounded-none",
           }}
           onOpenChange={setOpen}>
    <PopoverTrigger>
      <div className="h-full w-full text-right">
        <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle">
          more_vert
        </span>
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[205px] py-[8px] gap-[8px]">
        { (role?.admin || role?.privilages?.includes("ChangeTrackFormats")) &&
          <MenuItem icon="edit_note"
                    label={t('track_formats:edit_track_format:caption')}
                    onClick={handleEdit} />
        }
        { (role?.admin || role?.privilages?.includes("DeleteTrackFormats")) &&
          <MenuItem icon="delete"
                    color="!text-[--Warning]"
                    label={t('track_formats:delete_track_format')}
                    onClick={handleDelete} />
        }
      </FlexRow>
    </PopoverContent>
  </Popover>
  );
};

export default TrackFormats;
